<!--
  CustomizeModal.vue
  ダッシュボードカスタマイズモーダル
  
  Created: 2025-08-11
  Author: Claude
-->
<template>
  <transition name="modal-fade">
    <div class="modal-overlay" @click="closeModal">
      <div class="modal-container" @click.stop>
        <!-- モーダルヘッダー -->
        <div class="modal-header">
          <h3 class="modal-title">
            <i class="fas fa-cog mr-2"></i>
            ダッシュボードのカスタマイズ
          </h3>
          <button class="close-btn" @click="closeModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <!-- モーダルボディ -->
        <div class="modal-body">
          <!-- KPI選択セクション -->
          <div class="customize-section kpi-selection-section">
            <h4 class="section-title">
              <i class="fas fa-chart-line mr-2"></i>
              表示するKPI指標を選択
            </h4>
            <p class="section-description">
              ダッシュボードに表示したい指標を選択してください（最大20個まで）
            </p>
            
            <div class="selection-info-float">
              <span>選択中: {{ selectedKpis.length }}個</span>
              <span class="separator">|</span>
              <span>最大: 20個</span>
            </div>
            
            <!-- 検索ボックス -->
            <div class="search-box">
              <i class="fas fa-search"></i>
              <input
                v-model="searchQuery"
                type="text"
                placeholder="KPIを検索..."
                class="search-input"
              />
            </div>
            
            <div class="kpi-grid">
              <div 
                v-for="kpi in filteredKpis"
                :key="kpi.id"
                class="kpi-item"
                :class="{ 
                  selected: isSelected(kpi.id), 
                  disabled: !canSelect(kpi.id),
                  'summary-card': kpi.id === 'summaryOverview',
                  'task-card': kpi.id === 'todayTasks'
                }"
              >
                <label class="kpi-label">
                  <input 
                    type="checkbox"
                    :value="kpi.id"
                    v-model="selectedKpis"
                    :disabled="!canSelect(kpi.id)"
                    class="kpi-checkbox"
                  />
                  <div class="kpi-content">
                    <div class="kpi-icon" :style="{ backgroundColor: kpi.color + '20', color: kpi.color }">
                      <i :class="`fas fa-${kpi.icon}`"></i>
                    </div>
                    <div class="kpi-info">
                      <div class="kpi-name">{{ kpi.title }}</div>
                      <div class="kpi-unit">単位: {{ kpi.unit }}</div>
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </div>
          
          <!-- レイアウト設定セクション -->
          <div class="customize-section">
            <h4 class="section-title">
              <i class="fas fa-th mr-2"></i>
              レイアウト設定
            </h4>
            <p class="section-description">
              KPIカードの表示列数を設定します
            </p>
            
            <div class="layout-options">
              <label 
                v-for="option in layoutOptions"
                :key="option.value"
                class="layout-option"
                :class="{ selected: layoutMode === option.value }"
              >
                <input 
                  type="radio" 
                  :value="option.value" 
                  v-model="layoutMode"
                  class="layout-radio"
                />
                <div class="layout-content">
                  <div class="layout-icon">
                    <i :class="`fas fa-${option.icon}`"></i>
                  </div>
                  <div class="layout-info">
                    <div class="layout-name">{{ option.label }}</div>
                    <div class="layout-desc">{{ option.description }}</div>
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>
        
        <!-- モーダルフッター -->
        <div class="modal-footer">
          <button @click="resetToDefault" class="btn-secondary">
            <i class="fas fa-undo mr-2"></i>
            デフォルトに戻す
          </button>
          <div class="footer-actions">
            <button @click="closeModal" class="btn-cancel">
              キャンセル
            </button>
            <button @click="applyChanges" class="btn-primary" :disabled="selectedKpis.length === 0">
              <i class="fas fa-check mr-2"></i>
              適用
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'CustomizeModal',
  props: {
    availableKpis: {
      type: Array,
      required: true
    },
    visibleKpis: {
      type: Array,
      required: true
    },
    currentLayoutMode: {
      type: String,
      default: 'auto'
    }
  },
  
  data() {
    return {
      selectedKpis: [...this.visibleKpis],
      layoutMode: this.currentLayoutMode,
      searchQuery: '',
      
      // レイアウトオプション
      layoutOptions: [
        {
          value: 'auto',
          label: '自動レイアウト',
          icon: 'magic',
          description: '選択数に応じて最適化'
        },
        {
          value: 'grid3',
          label: '3列固定',
          icon: 'th',
          description: '常に3列で表示'
        },
        {
          value: 'grid4',
          label: '4列固定',
          icon: 'th-large',
          description: '常に4列で表示'
        }
      ]
    };
  },
  
  computed: {
    /**
     * 検索でフィルタリングされたKPIリスト
     */
    filteredKpis() {
      if (!this.searchQuery) {
        return this.availableKpis;
      }
      
      const query = this.searchQuery.toLowerCase();
      return this.availableKpis.filter(kpi => 
        kpi.title.toLowerCase().includes(query) ||
        kpi.id.toLowerCase().includes(query)
      );
    }
  },
  
  methods: {
    /**
     * KPIが選択されているか確認
     */
    isSelected(kpiId) {
      return this.selectedKpis.includes(kpiId);
    },
    
    /**
     * KPIが選択可能か確認
     */
    canSelect(kpiId) {
      // 既に選択されている場合は常に選択可能（チェック解除のため）
      if (this.isSelected(kpiId)) {
        return true;
      }
      // 20個未満なら選択可能
      return this.selectedKpis.length < 20;
    },
    
    /**
     * デフォルトに戻す
     */
    resetToDefault() {
      // デフォルトで有効なKPIを選択
      this.selectedKpis = this.availableKpis
        .filter(kpi => kpi.enabled)
        .map(kpi => kpi.id);
      this.layoutMode = 'auto';
    },
    
    /**
     * 変更を適用
     */
    applyChanges() {
      if (this.selectedKpis.length === 0) {
        alert('少なくとも1つのKPIを選択してください');
        return;
      }
      
      this.$emit('update', this.selectedKpis, this.layoutMode);
      this.closeModal();
    },
    
    /**
     * モーダルを閉じる
     */
    closeModal() {
      this.$emit('close');
    }
  }
}
</script>

<style lang="scss" scoped>
// モーダルオーバーレイ
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.modal-container {
  background: white;
  border-radius: 12px;
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

// モーダルヘッダー
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #e9ecef;
}

.modal-title {
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  
  i {
    color: #3498db;
  }
}

.close-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 50%;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f8f9fa;
    color: #495057;
  }
}

// モーダルボディ
.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.customize-section {
  margin-bottom: 32px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  &.kpi-selection-section {
    position: relative;
  }
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 8px 0;
  
  i {
    color: #3498db;
    font-size: 14px;
  }
}

.section-description {
  font-size: 13px;
  color: #6c757d;
  margin: 0 0 16px 0;
}

// KPIグリッド
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 12px;
}

.kpi-item {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover:not(.disabled) {
    border-color: #3498db;
    background: #f8f9fa;
  }
  
  &.selected {
    border-color: #3498db;
    background: rgba(52, 152, 219, 0.05);
  }
  
  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
}

.kpi-label {
  display: block;
  padding: 12px;
  cursor: pointer;
  
  .disabled & {
    cursor: not-allowed;
  }
}

.kpi-checkbox {
  display: none;
}

.kpi-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.kpi-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 18px;
}

.kpi-info {
  flex: 1;
}

.kpi-name {
  font-size: 14px;
  font-weight: 500;
  color: #2c3e50;
  margin-bottom: 2px;
}

.kpi-unit {
  font-size: 12px;
  color: #6c757d;
}

.selection-info-float {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: #6c757d;
  background: #f8f9fa;
  padding: 8px 16px;
  border-radius: 6px;
  white-space: nowrap;
  
  .separator {
    color: #dee2e6;
  }
}

// 検索ボックス
.search-box {
  margin-bottom: 16px;
  position: relative;
  
  i {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #6c757d;
  }
  
  .search-input {
    width: 100%;
    padding: 10px 12px 10px 36px;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    font-size: 14px;
    transition: all 0.3s ease;
    
    &:focus {
      outline: none;
      border-color: #3498db;
      box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
    }
  }
}

// レイアウトオプション
.layout-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.layout-option {
  display: block;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #3498db;
    background: #f8f9fa;
  }
  
  &.selected {
    border-color: #3498db;
    background: rgba(52, 152, 219, 0.05);
  }
}

.layout-radio {
  display: none;
}

.layout-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.layout-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  border-radius: 8px;
  color: #3498db;
  font-size: 18px;
}

.layout-info {
  flex: 1;
}

.layout-name {
  font-size: 14px;
  font-weight: 500;
  color: #2c3e50;
  margin-bottom: 2px;
}

.layout-desc {
  font-size: 12px;
  color: #6c757d;
}

// プリセットオプション
.preset-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
}

.preset-btn {
  padding: 12px 16px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 13px;
  color: #495057;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #e9ecef;
    border-color: #adb5bd;
  }
  
  i {
    color: #3498db;
  }
}

// モーダルフッター
.modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-top: 1px solid #e9ecef;
  background: #f8f9fa;
  border-radius: 0 0 12px 12px;
}

.footer-actions {
  display: flex;
  gap: 12px;
}

// ボタンスタイル
.btn-primary {
  padding: 10px 20px;
  background: #3498db;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background: #2980b9;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.btn-secondary {
  padding: 10px 20px;
  background: white;
  color: #495057;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f8f9fa;
    border-color: #adb5bd;
  }
}

.btn-cancel {
  padding: 10px 20px;
  background: transparent;
  color: #6c757d;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f8f9fa;
    border-color: #adb5bd;
  }
}

// ユーティリティクラス
.mr-2 {
  margin-right: 8px;
}

// アニメーション
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
  
  .modal-container {
    transition: transform 0.3s ease;
  }
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
  
  .modal-container {
    transform: scale(0.9);
  }
}

// レスポンシブ対応
@media (max-width: 768px) {
  .modal-container {
    max-width: 100%;
    max-height: 100vh;
    border-radius: 0;
  }
  
  .kpi-grid {
    grid-template-columns: 1fr;
  }
  
  .layout-options {
    grid-template-columns: 1fr;
  }
  
  .modal-footer {
    flex-direction: column;
    gap: 12px;
    
    .footer-actions {
      width: 100%;
      
      button {
        flex: 1;
      }
    }
  }
}

@media (max-width: 576px) {
  .modal-header {
    padding: 16px;
  }
  
  .modal-body {
    padding: 16px;
  }
  
  .modal-title {
    font-size: 18px;
  }
  
  .preset-options {
    grid-template-columns: 1fr;
  }
}
</style>


// WEBPACK FOOTER //
// src/views/DashBoard/Report/components/CustomizeModal.vue